/**
 * @file     aiSeed.ts
 * @purpose  One-shot handoff of a first message from an entry surface (the
 *           homepage hero) to the /properties AI Search, which auto-sends it
 *           on mount. The prose arrives as `aiq` on a shareable URL and is
 *           queued here once per tab, so a refresh never sends it twice.
 */
import { reportSilentFailure } from '@/core/observability/reportSilentFailure';

export const AI_SEED_KEY = 'relta:ai-seed';

export function stashAiSeed(text: string): void {
  try { sessionStorage.setItem(AI_SEED_KEY, text); } catch { /* private mode: skip handoff */ }
}

const AI_INTAKE_KEY = 'relta:ai-intake';

/**
 * Queue the `aiq` prose a /properties URL arrived with, once per tab: a
 * refresh or a back navigation to the same URL must not send it again.
 */
export function queueAiSeedFromUrl(text: string): void {
  try {
    if (sessionStorage.getItem(AI_INTAKE_KEY) === text) return;
    sessionStorage.setItem(AI_INTAKE_KEY, text);
    sessionStorage.setItem(AI_SEED_KEY, text);
  } catch (err) {
    // Private mode or storage disabled: the handoff is skipped, the search still runs.
    reportSilentFailure(err, { area: 'ai-seed/queue' });
  }
}

/** A fresh search from an entry surface may repeat the last prose on purpose. */
export function clearAiSeedIntake(): void {
  try {
    sessionStorage.removeItem(AI_INTAKE_KEY);
  } catch (err) {
    reportSilentFailure(err, { area: 'ai-seed/clear' });
  }
}

/** Read and clear the seed. Returns null when there is nothing pending. */
export function takeAiSeed(): string | null {
  try {
    const seed = sessionStorage.getItem(AI_SEED_KEY);
    if (seed) sessionStorage.removeItem(AI_SEED_KEY);
    return seed;
  } catch {
    return null;
  }
}
